import {
  intranetBirthdayListResponseSchema,
  intranetColleagueListResponseSchema,
  intranetColleagueProfileSchema,
  intranetContactListResponseSchema,
  intranetContactSchema,
  intranetLinkListResponseSchema,
  intranetLinkSchema,
  roleOptionListSchema,
  intranetModuleInstanceListResponseSchema,
  intranetModuleInstanceSchema,
  intranetModuleKindListResponseSchema,
  intranetModuleReportListResponseSchema,
  intranetModuleReportSchema,
  weeklyPlanWeekSchema,
  type WeeklyPlanSave,
  siteDiaryDeliveryResultSchema,
  intranetQuickAccessResponseSchema,
  intranetSearchResponseSchema,
  intranetWidgetListResponseSchema,
  intranetWidgetSchema,
  submitIntranetModuleReportResponseSchema,
  type CreateIntranetContactRequest,
  type CreateIntranetLinkRequest,
  type CreateIntranetModuleInstanceRequest,
  type IntranetColleagueQuery,
  type IntranetSearchQuery,
  type PutIntranetModuleDraftRequest,
  type ReorderIntranetModulePhotosRequest,
  type SubmitIntranetModuleReportRequest,
  type UpdateIntranetContactRequest,
  type UpdateIntranetLinkRequest,
  type UpdateIntranetModuleInstanceRequest,
  type UpdateIntranetWidgetRequest,
} from '@gb-ticket/shared-types';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';
import { intranetQueryKey } from '@/products/intranet/features/intranet/use-intranet';

const okSchema = z.object({ ok: z.literal(true) });

const searchKey = [...intranetQueryKey, 'search'] as const;
const colleaguesKey = [...intranetQueryKey, 'colleagues'] as const;
const birthdaysKey = [...intranetQueryKey, 'birthdays'] as const;
const contactsKey = [...intranetQueryKey, 'contacts'] as const;
const linksKey = [...intranetQueryKey, 'links'] as const;
const widgetsKey = [...intranetQueryKey, 'widgets'] as const;
const modulesKey = [...intranetQueryKey, 'modules'] as const;
const moduleKindsKey = [...modulesKey, 'kinds'] as const;
const quickAccessKey = [...intranetQueryKey, 'quick-access'] as const;

export function useIntranetSearch(query: IntranetSearchQuery, enabled: boolean) {
  return useQuery({
    queryKey: [...searchKey, query],
    queryFn: () =>
      apiFetch(
        `/intranet/search${toSearchParams({
          q: query.q,
          kind: query.kind,
          categoryId: query.categoryId,
        })}`,
        intranetSearchResponseSchema,
      ),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useIntranetColleagues(query: IntranetColleagueQuery, enabled = true) {
  return useQuery({
    queryKey: [...colleaguesKey, query],
    queryFn: () =>
      apiFetch(
        `/intranet/colleagues${toSearchParams({
          q: query.q,
          departmentId: query.departmentId,
          page: query.page,
          pageSize: query.pageSize,
        })}`,
        intranetColleagueListResponseSchema,
      ),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** Public card for one colleague. Loads only while `userId` is set. */
export function useIntranetColleagueProfile(userId: string | null) {
  return useQuery({
    queryKey: [...colleaguesKey, 'profile', userId],
    queryFn: () => apiFetch(`/intranet/colleagues/${userId}`, intranetColleagueProfileSchema),
    enabled: Boolean(userId),
    retry: false,
  });
}

export function useIntranetWidgets() {
  return useQuery({
    queryKey: widgetsKey,
    queryFn: () => apiFetch('/intranet/widgets', intranetWidgetListResponseSchema),
  });
}

export function useUpdateIntranetWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateIntranetWidgetRequest }) =>
      apiFetch(`/intranet/widgets/${id}`, intranetWidgetSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: widgetsKey }),
  });
}

export function useIntranetBirthdays() {
  return useQuery({
    queryKey: birthdaysKey,
    queryFn: () => apiFetch('/intranet/birthdays', intranetBirthdayListResponseSchema),
  });
}

export function useIntranetContacts() {
  return useQuery({
    queryKey: contactsKey,
    queryFn: () => apiFetch('/intranet/contacts', intranetContactListResponseSchema),
  });
}

export function useIntranetLinks() {
  return useQuery({
    queryKey: linksKey,
    queryFn: () => apiFetch('/intranet/links', intranetLinkListResponseSchema),
  });
}

export function useIntranetLinkAudienceRoles() {
  return useQuery({
    queryKey: [...linksKey, 'audience-roles'] as const,
    queryFn: () => apiFetch('/intranet/links/audience-roles', roleOptionListSchema),
  });
}

function invalidateLinks(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: linksKey });
  void queryClient.invalidateQueries({ queryKey: quickAccessKey });
}

export function useIntranetQuickAccess() {
  return useQuery({
    queryKey: quickAccessKey,
    queryFn: () => apiFetch('/intranet/quick-access', intranetQuickAccessResponseSchema),
  });
}

export function useIntranetModuleKinds() {
  return useQuery({
    queryKey: moduleKindsKey,
    queryFn: () => apiFetch('/intranet/modules/kinds', intranetModuleKindListResponseSchema),
  });
}

export function useIntranetModules() {
  return useQuery({
    queryKey: modulesKey,
    queryFn: () => apiFetch('/intranet/modules', intranetModuleInstanceListResponseSchema),
  });
}

function invalidateModules(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: modulesKey });
  void queryClient.invalidateQueries({ queryKey: quickAccessKey });
}

export function useCreateIntranetModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateIntranetModuleInstanceRequest) =>
      apiFetch('/intranet/modules', intranetModuleInstanceSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateModules(queryClient),
  });
}

export function useUpdateIntranetModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateIntranetModuleInstanceRequest }) =>
      apiFetch(`/intranet/modules/${id}`, intranetModuleInstanceSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateModules(queryClient),
  });
}

export function useDeleteIntranetModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/intranet/modules/${id}`, okSchema, { method: 'DELETE' }),
    onSuccess: () => invalidateModules(queryClient),
  });
}

export function useUploadIntranetModuleLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const body = new FormData();
      body.append('file', file);
      return apiFetch(`/intranet/modules/${id}/logo`, intranetModuleInstanceSchema, {
        method: 'POST',
        body,
      });
    },
    onSuccess: () => invalidateModules(queryClient),
  });
}

function moduleReportsKey(instanceId: string) {
  return [...modulesKey, instanceId, 'reports'] as const;
}

function invalidateModuleReports(
  queryClient: ReturnType<typeof useQueryClient>,
  instanceId: string,
) {
  void queryClient.invalidateQueries({ queryKey: moduleReportsKey(instanceId) });
  void queryClient.invalidateQueries({ queryKey: quickAccessKey });
}

export function useIntranetModule(instanceId: string) {
  return useQuery({
    queryKey: [...modulesKey, instanceId],
    enabled: instanceId.length > 0,
    queryFn: () => apiFetch(`/intranet/modules/${instanceId}`, intranetModuleInstanceSchema),
  });
}

function weekQuery(isoYear?: number, isoWeek?: number, siteId?: string): string {
  const params = new URLSearchParams();
  if (isoYear !== undefined && isoWeek !== undefined) {
    params.set('jahr', String(isoYear));
    params.set('woche', String(isoWeek));
  }
  if (siteId) {
    params.set('standort', siteId);
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

function weekKey(instanceId: string, isoYear?: number, isoWeek?: number, siteId?: string) {
  return [
    ...modulesKey,
    instanceId,
    'week',
    isoYear ?? null,
    isoWeek ?? null,
    siteId ?? null,
  ] as const;
}

export function useWeeklyPlanWeek(
  instanceId: string,
  isoYear?: number,
  isoWeek?: number,
  siteId?: string,
) {
  return useQuery({
    queryKey: weekKey(instanceId, isoYear, isoWeek, siteId),
    enabled: instanceId.length > 0,
    queryFn: () =>
      apiFetch(
        `/intranet/modules/${instanceId}/week${weekQuery(isoYear, isoWeek, siteId)}`,
        weeklyPlanWeekSchema,
      ),
  });
}

export function useSaveWeeklyPlan(
  instanceId: string,
  isoYear?: number,
  isoWeek?: number,
  siteId?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: WeeklyPlanSave) =>
      apiFetch(
        `/intranet/modules/${instanceId}/week${weekQuery(isoYear, isoWeek, siteId)}`,
        weeklyPlanWeekSchema,
        {
          method: 'PUT',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: (week) => {
      queryClient.setQueryData(weekKey(instanceId, isoYear, isoWeek, siteId), week);
    },
  });
}

export function usePublishWeeklyPlan(
  instanceId: string,
  isoYear?: number,
  isoWeek?: number,
  siteId?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: WeeklyPlanSave) =>
      apiFetch(
        `/intranet/modules/${instanceId}/week/publish${weekQuery(isoYear, isoWeek, siteId)}`,
        weeklyPlanWeekSchema,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (week) => {
      queryClient.setQueryData(weekKey(instanceId, isoYear, isoWeek, siteId), week);
    },
  });
}

export function useIntranetModuleReports(instanceId: string) {
  return useQuery({
    queryKey: moduleReportsKey(instanceId),
    enabled: instanceId.length > 0,
    queryFn: () =>
      apiFetch(`/intranet/modules/${instanceId}/reports`, intranetModuleReportListResponseSchema),
  });
}

export function usePutIntranetModuleDraft(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PutIntranetModuleDraftRequest) =>
      apiFetch(`/intranet/modules/${instanceId}/draft`, intranetModuleReportSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateModuleReports(queryClient, instanceId),
  });
}

export function useSubmitIntranetModuleReport(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SubmitIntranetModuleReportRequest) =>
      apiFetch(`/intranet/modules/${instanceId}/submit`, submitIntranetModuleReportResponseSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateModuleReports(queryClient, instanceId),
  });
}

export function useAddIntranetModulePhoto(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return apiFetch(`/intranet/modules/${instanceId}/photos`, intranetModuleReportSchema, {
        method: 'POST',
        body,
      });
    },
    onSuccess: () => invalidateModuleReports(queryClient, instanceId),
  });
}

export function useRemoveIntranetModulePhoto(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (photoId: string) =>
      apiFetch(`/intranet/modules/${instanceId}/photos/${photoId}`, intranetModuleReportSchema, {
        method: 'DELETE',
      }),
    onSuccess: () => invalidateModuleReports(queryClient, instanceId),
  });
}

export function useReorderIntranetModulePhotos(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReorderIntranetModulePhotosRequest) =>
      apiFetch(`/intranet/modules/${instanceId}/photos/order`, intranetModuleReportSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateModuleReports(queryClient, instanceId),
  });
}

export function useResendIntranetModuleReport(instanceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) =>
      apiFetch(
        `/intranet/modules/${instanceId}/reports/${reportId}/send`,
        siteDiaryDeliveryResultSchema,
        { method: 'POST' },
      ),
    onSuccess: () => invalidateModuleReports(queryClient, instanceId),
  });
}

export function useCreateIntranetContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateIntranetContactRequest) =>
      apiFetch('/intranet/contacts', intranetContactSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: contactsKey }),
  });
}

export function useUpdateIntranetContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateIntranetContactRequest }) =>
      apiFetch(`/intranet/contacts/${id}`, intranetContactSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: contactsKey }),
  });
}

export function useDeleteIntranetContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/intranet/contacts/${id}`, okSchema, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: contactsKey }),
  });
}

export function useCreateIntranetLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateIntranetLinkRequest) =>
      apiFetch('/intranet/links', intranetLinkSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateLinks(queryClient),
  });
}

export function useUpdateIntranetLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateIntranetLinkRequest }) =>
      apiFetch(`/intranet/links/${id}`, intranetLinkSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => invalidateLinks(queryClient),
  });
}

export function useDeleteIntranetLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/intranet/links/${id}`, okSchema, { method: 'DELETE' }),
    onSuccess: () => invalidateLinks(queryClient),
  });
}
